import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { Plus, Trash2, Edit2, ArrowUp, ArrowDown, Briefcase, ChevronDown, ChevronUp } from "lucide-react";
import {
  experienceContentSchema,
  type ExperienceContent,
  type ExperienceItem,
} from "@profolio/validators";

interface ExperienceEditorProps {
  content: Record<string, unknown>;
  onChange: (content: ExperienceContent) => void;
}

const emptyItem: ExperienceItem = {
  organization: "",
  role: "",
  department: "",
  startDate: "",
  endDate: "",
  current: false,
  location: "",
  description: "",
};

export function ExperienceEditor({ content, onChange }: ExperienceEditorProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<ExperienceItem>(emptyItem);
  const [formError, setFormError] = useState<string | null>(null);
  const [modalDetailsOpen, setModalDetailsOpen] = useState(false);

  const parseResult = experienceContentSchema.safeParse(content);
  const data: ExperienceContent = parseResult.success
    ? parseResult.data
    : { heading: "Experience", items: [] };

  const heading = data.heading || "Experience";
  const items: ExperienceItem[] = data.items || [];

  const notifyChange = (newHeading: string, newItems: ExperienceItem[]) => {
    onChange({
      heading: newHeading,
      items: newItems,
    });
  };

  const openAddModal = () => {
    setEditingIndex(null);
    setFormData({ ...emptyItem });
    setFormError(null);
    setModalDetailsOpen(false);
    setModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setFormData({ ...items[index] });
    setFormError(null);
    setModalDetailsOpen(true);
    setModalOpen(true);
  };

  const handleSaveItem = () => {
    if (!formData.organization.trim()) {
      setFormError("Organization / Institution is required.");
      return;
    }
    if (!formData.role.trim()) {
      setFormError("Role / Designation is required.");
      return;
    }
    if (!formData.startDate.trim()) {
      setFormError("Start date is required (e.g. 2020 or Aug 2020).");
      return;
    }

    let updatedItems: ExperienceItem[];
    if (editingIndex !== null) {
      updatedItems = [...items];
      updatedItems[editingIndex] = formData;
    } else {
      updatedItems = [formData, ...items];
    }

    notifyChange(heading, updatedItems);
    setModalOpen(false);
  };

  const handleDeleteItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    notifyChange(heading, updated);
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const updated = [...items];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    notifyChange(heading, updated);
  };

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="exp-heading" className="text-xs font-semibold">
          Section Heading
        </Label>
        <Input
          id="exp-heading"
          value={heading}
          onChange={(e) => notifyChange(e.target.value, items)}
          placeholder="Experience & Appointments"
          className="mt-1 text-xs"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-xs font-semibold">Positions & Appointments ({items.length})</Label>
            <p className="text-[11px] text-muted-foreground">
              Add your academic appointments, visiting fellowships, and leadership roles.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={openAddModal}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Position
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title="No appointments added yet"
            description="List your faculty roles, visiting professorships, postdocs, and academic appointments."
            actionLabel="Add First Position"
            onAction={openAddModal}
          />
        ) : (
          <div className="space-y-2.5">
            {items.map((item, index) => (
              <div
                key={item.id || index}
                className="flex items-start justify-between gap-3 rounded-lg border bg-card p-3.5 shadow-2xs hover:border-primary/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-xs text-foreground">{item.role}</span>
                    <span className="text-muted-foreground text-xs">at</span>
                    <span className="font-medium text-xs text-primary">{item.organization}</span>
                    {item.current && <Badge variant="secondary" className="text-[10px]">Current</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-1 flex-wrap">
                    <span>📅 {item.startDate} – {item.current ? "Present" : item.endDate || "Present"}</span>
                    {item.location && <span>📍 {item.location}</span>}
                    {item.department && <span>🏛️ {item.department}</span>}
                  </div>
                  {item.description && (
                    <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                      {item.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={index === 0}
                    onClick={() => moveItem(index, "up")}
                    className="h-7 w-7 text-muted-foreground"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={index === items.length - 1}
                    onClick={() => moveItem(index, "down")}
                    className="h-7 w-7 text-muted-foreground"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditModal(index)}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteItem(index)}
                    className="h-7 w-7 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Add / Edit Experience Dialog with Progressive Disclosure ── */}
      <Dialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editingIndex !== null ? "Edit Position" : "Add Position"}
        description="Fill in your appointment details below."
      >
        <div className="space-y-4 pt-2">
          {formError && (
            <div className="rounded-md bg-destructive/15 p-2.5 text-xs text-destructive font-medium">
              {formError}
            </div>
          )}

          {/* 1. Essential Fields */}
          <div>
            <Label htmlFor="exp-org" className="text-xs font-semibold">
              Organization / University *
            </Label>
            <Input
              id="exp-org"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
              placeholder="e.g. University of Colombo"
              className="mt-1 text-xs"
            />
          </div>

          <div>
            <Label htmlFor="exp-role" className="text-xs font-semibold">
              Role / Designation *
            </Label>
            <Input
              id="exp-role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              placeholder="e.g. Senior Lecturer in Computer Science"
              className="mt-1 text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-start" className="text-xs font-semibold">
                Start Date *
              </Label>
              <Input
                id="exp-start"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                placeholder="e.g. 2020 or Aug 2020"
                className="mt-1 text-xs"
              />
            </div>

            <div>
              <Label htmlFor="exp-end" className="text-xs font-semibold">
                End Date
              </Label>
              <Input
                id="exp-end"
                value={formData.endDate || ""}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                placeholder="e.g. 2024 or Present"
                disabled={formData.current}
                className="mt-1 text-xs"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.current}
              onCheckedChange={(checked) =>
                setFormData({
                  ...formData,
                  current: checked,
                  endDate: checked ? "" : formData.endDate,
                })
              }
            />
            <Label className="text-xs cursor-pointer">I currently hold this position</Label>
          </div>

          {/* 2. Progressive Disclosure: Location, Department, Details */}
          <div className="rounded-lg border bg-muted/20 overflow-hidden">
            <button
              type="button"
              onClick={() => setModalDetailsOpen(!modalDetailsOpen)}
              className="flex w-full items-center justify-between p-3 text-xs font-medium hover:bg-muted/40 transition-colors"
            >
              <span>Department, Location & Description</span>
              {modalDetailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {modalDetailsOpen && (
              <div className="p-3.5 pt-1 border-t space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="exp-dept" className="text-xs font-medium">
                      Department / Faculty
                    </Label>
                    <Input
                      id="exp-dept"
                      value={formData.department || ""}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      placeholder="e.g. Dept of Computer Science"
                      className="mt-1 text-xs"
                    />
                  </div>

                  <div>
                    <Label htmlFor="exp-loc" className="text-xs font-medium">
                      Location
                    </Label>
                    <Input
                      id="exp-loc"
                      value={formData.location || ""}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g. Colombo, Sri Lanka"
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="exp-desc" className="text-xs font-medium">
                    Description & Key Contributions
                  </Label>
                  <Textarea
                    id="exp-desc"
                    value={formData.description || ""}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Responsibilities, committee appointments, teaching loads..."
                    rows={3}
                    className="mt-1 text-xs"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              className="text-xs h-8"
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSaveItem} className="text-xs h-8 font-medium">
              {editingIndex !== null ? "Save Changes" : "Add Position"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
